/**
 * Applies the same broken-image fallback as {@link Crepe.Feature.ImageBlock}'s `onImageLoadError`
 * for inline `<img>` nodes (e.g. commonmark `image` inside table cells), which do not use the
 * image-block Vue viewer and therefore never receive an `onError` handler.
 */
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import { $prose } from '@milkdown/kit/utils';

const imageErrorFallbackKey = new PluginKey('brainfish-image-error-fallback');

function resolveFallbackHref(fallback: string): string {
  try {
    return new URL(fallback, document.baseURI).href;
  } catch {
    return fallback;
  }
}

function isAlreadyFallbackImage(img: HTMLImageElement, resolvedFallback: string): boolean {
  if (!img.src) return false;
  if (img.src === resolvedFallback) return true;
  // Bundled default asset path / custom URLs that include a stable filename

  return img.src.includes('image-error.svg');
}

export function createImageErrorFallbackProsePlugin(getFallbackSrc: () => string) {
  return new Plugin({
    key: imageErrorFallbackKey,
    view(editorView) {
      const onErrorCapture = (ev: Event) => {
        const target = ev.target;
        if (!(target instanceof HTMLImageElement)) return;
        if (!editorView.dom.contains(target)) return;

        const fallback = getFallbackSrc();
        if (!fallback) return;

        const resolved = resolveFallbackHref(fallback);
        if (isAlreadyFallbackImage(target, resolved)) return;

        target.src = fallback;
      };

      editorView.dom.addEventListener('error', onErrorCapture, true);

      return {
        destroy() {
          editorView.dom.removeEventListener('error', onErrorCapture, true);
        },
      };
    },
  });
}

/** Milkdown wrapper so `crepe.editor.use(...)` picks up the prose plugin. */
export function imageErrorFallbackMilkdownPlugin(getFallbackSrc: () => string) {
  return $prose(() => createImageErrorFallbackProsePlugin(getFallbackSrc));
}
